import { useState, type FormEvent } from 'react';
import { apiGet, apiPost, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';

/** GET /admin/service-card-templates response shape (ServiceCardTemplateService). */
interface ServiceCardTemplate {
  id: string;
  specialtyCode: string;
  serviceType: string;
  estimatedDuration: string;
  priceUsd: number;
  titleEn: string;
  titleEs: string;
  descriptionEn: string;
  descriptionEs: string;
  searchTags: string[];
  archived: boolean;
  createdBy: string;
}

/** Reuses the fixed 11-ServiceType list from the existing Service Cards admin, so the dropdown
 * here never drifts from what's actually valid — no separate hardcoded enum in this repo. */
interface ServiceCardId {
  id: string;
}

const EMPTY_FORM = {
  specialtyCode: '',
  serviceType: '',
  estimatedDuration: '',
  priceUsd: '',
  titleEn: '',
  titleEs: '',
  descriptionEn: '',
  descriptionEs: '',
  searchTags: '',
};

type TemplateForm = typeof EMPTY_FORM;

const templateToForm = (t: ServiceCardTemplate): TemplateForm => ({
  specialtyCode: t.specialtyCode,
  serviceType: t.serviceType,
  estimatedDuration: t.estimatedDuration,
  priceUsd: String(t.priceUsd),
  titleEn: t.titleEn,
  titleEs: t.titleEs,
  descriptionEn: t.descriptionEn,
  descriptionEs: t.descriptionEs,
  searchTags: t.searchTags.join(', '),
});

const formToBody = (form: TemplateForm) => ({
  specialtyCode: form.specialtyCode.trim(),
  serviceType: form.serviceType,
  estimatedDuration: form.estimatedDuration.trim(),
  priceUsd: Number(form.priceUsd),
  titleEn: form.titleEn.trim(),
  titleEs: form.titleEs.trim(),
  descriptionEn: form.descriptionEn.trim(),
  descriptionEs: form.descriptionEs.trim(),
  searchTags: form.searchTags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean),
});

const isFormValid = (form: TemplateForm) =>
  !!form.specialtyCode.trim() &&
  !!form.serviceType &&
  !!form.estimatedDuration.trim() &&
  Number.isFinite(Number(form.priceUsd)) &&
  Number(form.priceUsd) >= 0 &&
  !!form.titleEn.trim() &&
  !!form.titleEs.trim() &&
  !!form.descriptionEn.trim() &&
  !!form.descriptionEs.trim();

function TemplateFormFields({
  form,
  serviceTypeOptions,
  onChange,
}: {
  form: TemplateForm;
  serviceTypeOptions: string[];
  onChange: (next: TemplateForm) => void;
}) {
  return (
    <>
      <label>Specialty code</label>
      <input
        value={form.specialtyCode}
        onChange={(e) => onChange({ ...form, specialtyCode: e.target.value })}
        placeholder="general_medicine"
      />

      <label>Service type</label>
      <select value={form.serviceType} onChange={(e) => onChange({ ...form, serviceType: e.target.value })}>
        <option value="" disabled>
          Select a service type
        </option>
        {serviceTypeOptions.map((st) => (
          <option key={st} value={st}>
            {st}
          </option>
        ))}
      </select>

      <label>Estimated duration</label>
      <input
        value={form.estimatedDuration}
        onChange={(e) => onChange({ ...form, estimatedDuration: e.target.value })}
        placeholder="30 min"
      />

      <label>Price (USD)</label>
      <input
        type="number"
        min={0}
        step="0.01"
        value={form.priceUsd}
        onChange={(e) => onChange({ ...form, priceUsd: e.target.value })}
      />

      <label>Title (English)</label>
      <input value={form.titleEn} onChange={(e) => onChange({ ...form, titleEn: e.target.value })} />

      <label>Title (Spanish)</label>
      <input value={form.titleEs} onChange={(e) => onChange({ ...form, titleEs: e.target.value })} />

      <label>Description (English)</label>
      <textarea rows={2} value={form.descriptionEn} onChange={(e) => onChange({ ...form, descriptionEn: e.target.value })} />

      <label>Description (Spanish)</label>
      <textarea rows={2} value={form.descriptionEs} onChange={(e) => onChange({ ...form, descriptionEs: e.target.value })} />

      <label>Search tags (comma-separated, optional)</label>
      <input
        value={form.searchTags}
        onChange={(e) => onChange({ ...form, searchTags: e.target.value })}
        placeholder="telemedicine, video"
      />
    </>
  );
}

/**
 * ADM-51 (docs/admin-portal.md §17.7) — dynamic service card templates. Unlike `ServiceCards.tsx`
 * (which overrides price/duration/turnaround on the fixed 11 `ServiceType`s), this page manages a
 * genuinely open-ended catalog of admin-authored templates: create as many as needed, edit them,
 * and archive (no hard delete — mirrors `Specialties.tsx`'s reasoning: a specialist may already
 * have cloned a template into their own service card, so removing it out from under that
 * provenance link would be surprising). Specialists clone one of these into their own card via
 * the app's existing template-marketplace flow (`ServiceCardManager.tsx`), which now also reads
 * from `GET /service-card-templates` alongside its hardcoded starter templates.
 */
export function ServiceCardTemplatesPage() {
  const { data: templates, loading, error, reload } = useApiResource(
    () => apiGet<ServiceCardTemplate[]>('/admin/service-card-templates'),
    [],
  );
  const { data: serviceCards } = useApiResource(
    () => apiGet<ServiceCardId[]>('/admin/service-cards'),
    [],
  );
  const serviceTypeOptions = (serviceCards ?? []).map((c) => c.id);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<TemplateForm>(EMPTY_FORM);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (!isFormValid(form)) {
      setCreateError('All fields except search tags are required.');
      return;
    }
    setCreating(true);
    try {
      await apiPost('/admin/service-card-templates', formToBody(form));
      setForm(EMPTY_FORM);
      setShowForm(false);
      reload();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (t: ServiceCardTemplate) => {
    setEditingId(t.id);
    setEditForm(templateToForm(t));
    setActionError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id: string) => {
    if (!isFormValid(editForm)) {
      setActionError('All fields except search tags are required.');
      return;
    }
    setBusyId(id);
    setActionError(null);
    try {
      await apiPatch(`/admin/service-card-templates/${encodeURIComponent(id)}`, formToBody(editForm));
      setEditingId(null);
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update template');
    } finally {
      setBusyId(null);
    }
  };

  const toggleArchived = async (t: ServiceCardTemplate) => {
    setBusyId(t.id);
    setActionError(null);
    try {
      await apiPatch(`/admin/service-card-templates/${encodeURIComponent(t.id)}`, { archived: !t.archived });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update template');
    } finally {
      setBusyId(null);
    }
  };

  const showLoadingState = !templates && loading;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Service Card Templates</h1>
          <p>
            Create as many starter templates as you want per service type — specialists clone
            these into their own service cards from the app's template marketplace. Archiving
            hides a template from new clones without breaking cards already created from it;
            there is no hard delete.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'New template'}
        </button>
      </div>

      {showForm && (
        <form className="form-card" style={{ maxWidth: 'none', marginBottom: '1.5rem' }} onSubmit={submitCreate}>
          <h2>New template</h2>
          <TemplateFormFields form={form} serviceTypeOptions={serviceTypeOptions} onChange={setForm} />

          {createError && <p className="auth-error">{createError}</p>}

          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create template'}
          </button>
        </form>
      )}

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <DataTable
        id="service-card-templates"
        rows={templates}
        loading={showLoadingState}
        empty="No templates yet."
        noun="templates"
        getRowKey={(t) => t.id}
        /* The edit form used to replace the row it belonged to, which hid the very values
           being edited. It's a drawer under the row now, so the original stays in view. */
        expandedRow={(t) =>
          editingId !== t.id ? null : (
            <div className="form-card" style={{ maxWidth: 'none', margin: 0 }}>
              <TemplateFormFields form={editForm} serviceTypeOptions={serviceTypeOptions} onChange={setEditForm} />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button className="btn btn-primary" disabled={busyId === t.id} onClick={() => void saveEdit(t.id)}>
                  {busyId === t.id ? 'Saving…' : 'Save'}
                </button>
                <button className="btn" onClick={cancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          )
        }
        columns={[
          { key: 'specialty', header: 'Specialty', value: (t) => t.specialtyCode, filter: 'select' },
          { key: 'serviceType', header: 'Service type', value: (t) => t.serviceType, filter: 'select' },
          { key: 'titleEn', header: 'Title (EN)', value: (t) => t.titleEn },
          { key: 'duration', header: 'Duration', value: (t) => t.estimatedDuration },
          {
            key: 'price',
            header: 'Price',
            value: (t) => t.priceUsd,
            align: 'right',
            render: (t) => `$${t.priceUsd.toFixed(2)}`,
          },
          {
            key: 'status',
            header: 'Status',
            value: (t) => (t.archived ? 'Archived' : 'Active'),
            filter: 'select',
            render: (t) => (
              <StatusBadge label={t.archived ? 'Archived' : 'Active'} tone={t.archived ? 'neutral' : 'good'} />
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (t) => (
              <div className="row-actions">
                <button className="btn" disabled={busyId === t.id} onClick={() => startEdit(t)}>
                  {editingId === t.id ? 'Editing' : 'Edit'}
                </button>
                <button
                  className={`btn ${t.archived ? 'btn-primary' : 'btn-danger'}`}
                  disabled={busyId === t.id}
                  onClick={() => void toggleArchived(t)}
                >
                  {t.archived ? 'Restore' : 'Archive'}
                </button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
